import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BioComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';

interface BioSettingsProps {
  component: BioComponent;
}

export const BioSettings: React.FC<BioSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<BioComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.bio.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="default">{t('settings.common.styles.default')}</option>
          <option value="modern">{t('settings.common.styles.modern')}</option>
          <option value="minimal">{t('settings.common.styles.minimal')}</option>
          <option value="quote">{t('settings.common.styles.quote')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.bio.content')}</label>
        <textarea
          value={component.settings.content}
          onChange={(e) => handleUpdate({ content: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.bio.contentPlaceholder')}
          rows={5}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.fontSizePx', { value: component.settings.fontSize })}
        </label>
        <input
          type="range"
          min="12"
          max="24"
          value={component.settings.fontSize}
          onChange={(e) => handleUpdate({ fontSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.alignment')}</label>
        <select
          value={component.settings.alignment}
          onChange={(e) => handleUpdate({ alignment: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="left">{t('settings.common.alignLeft')}</option>
          <option value="center">{t('settings.common.alignCenter')}</option>
          <option value="right">{t('settings.common.alignRight')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
